import React, { useState } from 'react';
import { 
  FileDown, 
  Plus, 
  Trash2, 
  X,
  Layers,
  Edit3,
  FileSpreadsheet,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import * as XLSX from 'xlsx';

const ExportStudents = () => {
  const defaultColumns = ['S.N.', 'Full Name', 'Phone', 'Email', 'Remarks'];
  
  const createNewSheet = (name = 'Sheet 1') => ({
    id: Date.now(),
    name,
    columns: [...defaultColumns],
    rows: [{ id: 1, 'S.N.': '1', 'Full Name': '', 'Phone': '', 'Email': '', 'Remarks': '' }]
  });

  const [sheets, setSheets] = useState([createNewSheet()]);
  const [activeSheetId, setActiveSheetId] = useState(sheets[0].id);
  const [exporting, setExporting] = useState(false);
  const [newColumnName, setNewColumnName] = useState('');
  const [rowsToAddCount, setRowsToAddCount] = useState(1);

  const activeSheet = sheets.find(s => s.id === activeSheetId) || sheets[0];

  const updateActiveSheet = (updates) => {
    setSheets(sheets.map(s => s.id === activeSheetId ? { ...s, ...updates } : s));
  };

  const addSheet = () => {
    const name = `Sheet ${sheets.length + 1}`;
    const newSheet = createNewSheet(name);
    setSheets([...sheets, newSheet]);
    setActiveSheetId(newSheet.id);
  };

  const removeSheet = (id, e) => {
    e.stopPropagation();
    if (sheets.length === 1) return;
    const newSheets = sheets.filter(s => s.id !== id);
    setSheets(newSheets);
    if (activeSheetId === id) {
      setActiveSheetId(newSheets[0].id);
    }
  };

  const renameSheet = (name) => {
    updateActiveSheet({ name });
  };

  const addRow = (count = 1) => {
    const numToAdd = parseInt(count) || 1;
    let newRows = [...activeSheet.rows];
    let lastId = newRows.length > 0 ? Math.max(...newRows.map(r => r.id)) : 0;
    
    for (let i = 0; i < numToAdd; i++) {
      lastId++;
      const nextSN = newRows.length + 1;
      const newRow = { id: lastId };
      activeSheet.columns.forEach(col => {
        if (col === 'S.N.') {
          newRow[col] = nextSN.toString();
        } else {
          newRow[col] = '';
        }
      });
      newRows.push(newRow);
    }
    
    updateActiveSheet({ rows: newRows });
  };

  const removeRow = (rowId) => {
    if (activeSheet.rows.length === 1) {
      const resetRow = { id: 1, ...Object.fromEntries(activeSheet.columns.map(c => [c, ''])) };
      updateActiveSheet({ rows: [resetRow] });
      return;
    }
    updateActiveSheet({ rows: activeSheet.rows.filter(r => r.id !== rowId) });
  };

  const addColumn = () => {
    if (!newColumnName || activeSheet.columns.includes(newColumnName)) return;
    const newColumns = [...activeSheet.columns, newColumnName];
    const newRows = activeSheet.rows.map(row => ({ ...row, [newColumnName]: '' }));
    updateActiveSheet({ columns: newColumns, rows: newRows });
    setNewColumnName('');
  };

  const renameColumn = (oldName, newName) => {
    if (!newName || newName === oldName || activeSheet.columns.includes(newName)) return;
    
    const newColumns = activeSheet.columns.map(c => c === oldName ? newName : c);
    const newRows = activeSheet.rows.map(row => {
      const { [oldName]: val, ...rest } = row;
      return { ...rest, [newName]: val || '' };
    });
    
    updateActiveSheet({ columns: newColumns, rows: newRows });
  };

  const moveColumn = (index, direction) => {
    const newColumns = [...activeSheet.columns];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newColumns.length) return;
    
    // Swap columns
    [newColumns[index], newColumns[targetIndex]] = [newColumns[targetIndex], newColumns[index]];
    updateActiveSheet({ columns: newColumns });
  };

  const removeColumn = (colName) => {
    if (activeSheet.columns.length === 1) return;
    const newColumns = activeSheet.columns.filter(c => c !== colName);
    const newRows = activeSheet.rows.map(row => {
      const { [colName]: _, ...rest } = row;
      return rest;
    });
    updateActiveSheet({ columns: newColumns, rows: newRows });
  };

  const handleCellChange = (rowId, column, value) => {
    const newRows = activeSheet.rows.map(row => 
      row.id === rowId ? { ...row, [column]: value } : row
    );
    updateActiveSheet({ rows: newRows });
  };

  const handleExport = () => {
    setExporting(true);
    try {
      const workbook = XLSX.utils.book_new();
      
      sheets.forEach(sheet => {
        // Ensure data is in correct column order for each sheet
        const dataToExport = sheet.rows.map(row => {
          const rowData = {};
          sheet.columns.forEach(col => {
            rowData[col] = row[col] || '';
          });
          return rowData;
        });
        
        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        XLSX.utils.book_append_sheet(workbook, worksheet, sheet.name);
      });
      
      const fileName = `${activeSheet.name.replace(/[^a-z0-9]/gi, '_')}_List_${new Date().toLocaleDateString().replace(/\//g, '-')}.xlsx`;
      XLSX.writeFile(workbook, fileName);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to generate Excel file.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 border-b pb-6 px-1">
        <div>
          <h1 className="text-2xl font-black text-gray-900 tracking-tight flex items-center gap-2">
            <FileSpreadsheet className="text-indigo-600" />
            Workbook Builder
          </h1>
          <p className="text-sm font-medium text-gray-500">Design custom multi-sheet Excel files.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={handleExport}
            disabled={exporting}
            className="px-8 py-3 bg-emerald-600 text-white rounded-2xl font-black text-sm hover:bg-emerald-700 shadow-lg shadow-emerald-100 transition-all flex items-center gap-3 active:scale-95"
          >
            {exporting ? 'GENERATING...' : (
              <>
                <FileDown size={20} /> DOWNLOAD WORKBOOK (.XLSX)
              </>
            )}
          </button>
        </div>
      </div>

      {/* Tabs / Sheets Navigation */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
        {sheets.map(sheet => (
          <div
            key={sheet.id}
            onClick={() => setActiveSheetId(sheet.id)}
            className={`group relative flex items-center gap-3 px-6 py-3 rounded-2xl cursor-pointer transition-all border-2 shrink-0 ${
              activeSheetId === sheet.id 
                ? 'bg-white border-indigo-600 text-indigo-700 shadow-md translate-y-[-2px]' 
                : 'bg-gray-50 border-transparent text-gray-500 hover:bg-gray-100'
            }`}
          >
            <Layers size={16} className={activeSheetId === sheet.id ? 'text-indigo-600' : 'text-gray-400'} />
            <span className="text-sm font-black uppercase tracking-tight">{sheet.name}</span>
            {sheets.length > 1 && (
              <button 
                onClick={(e) => removeSheet(sheet.id, e)}
                className="opacity-0 group-hover:opacity-100 p-1 hover:bg-rose-100 hover:text-rose-600 rounded-full transition-all"
              >
                <X size={12} />
              </button>
            )}
          </div>
        ))}
        <button
          onClick={addSheet}
          className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl hover:bg-indigo-600 hover:text-white transition-all shadow-sm flex items-center gap-2 shrink-0"
          title="Add New Sheet"
        >
          <Plus size={20} />
          <span className="text-xs font-black uppercase tracking-widest pr-2">Add Sheet</span>
        </button>
      </div>

      {/* Active Sheet Editing Area */}
      <div className="card bg-white border border-gray-100 shadow-xl rounded-3xl overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500 w-full">
        {/* Sheet Controls */}
        <div className="p-6 bg-gray-50/50 border-b border-gray-100 flex flex-col md:flex-row gap-6 justify-between items-start md:items-center">
          <div className="flex items-center gap-4 w-full md:w-auto">
             <div className="bg-white p-1 rounded-2xl border border-gray-200 shadow-sm flex items-center flex-1 md:flex-none">
                <div className="pl-4 pr-2 text-indigo-600">
                  <Edit3 size={16} />
                </div>
                <input 
                  type="text" 
                  value={activeSheet.name}
                  onChange={(e) => renameSheet(e.target.value)}
                  placeholder="Sheet name..."
                  className="bg-transparent py-2.5 text-sm font-black text-gray-800 outline-none w-full md:w-48"
                />
             </div>
          </div>

          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="flex items-center gap-2 bg-white p-1.5 rounded-2xl border border-gray-200 shadow-sm flex-1 md:flex-none">
              <input 
                type="text" 
                placeholder="New column name..."
                value={newColumnName}
                onChange={(e) => setNewColumnName(e.target.value)}
                className="bg-transparent px-3 py-1 text-xs font-bold outline-none w-full md:w-40"
                onKeyPress={(e) => e.key === 'Enter' && addColumn()}
              />
              <button
                onClick={addColumn}
                className="p-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-all shadow-md"
                title="Add Column"
              >
                <Plus size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Spreadsheet Table */}
        <div className="overflow-x-auto pb-4 scrollbar-thin scrollbar-thumb-gray-200">
          <table className="w-full text-left border-collapse min-w-[1000px]">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                {activeSheet.columns.map((col, idx) => (
                  <th key={col} className={`px-2 py-3 text-[10px] font-black text-gray-400 uppercase tracking-widest group/col relative ${col === 'S.N.' ? 'w-20' : ''}`}>
                    <div className="flex items-center bg-transparent group-hover/col:bg-white rounded-lg transition-all border border-transparent group-hover/col:border-gray-200">
                      {/* Move Buttons */}
                      <div className="flex flex-col opacity-0 group-hover/col:opacity-100 transition-opacity border-r border-gray-100 pr-1">
                        <button 
                          onClick={() => moveColumn(idx, -1)}
                          disabled={idx === 0}
                          className="hover:text-indigo-600 disabled:opacity-0 p-0.5"
                          title="Move Left"
                        >
                          <ChevronLeft size={10} />
                        </button>
                        <button 
                          onClick={() => moveColumn(idx, 1)}
                          disabled={idx === activeSheet.columns.length - 1}
                          className="hover:text-indigo-600 disabled:opacity-0 p-0.5"
                          title="Move Right"
                        >
                          <ChevronRight size={10} />
                        </button>
                      </div>
                      
                      <input 
                        type="text"
                        defaultValue={col}
                        onBlur={(e) => renameColumn(col, e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && e.target.blur()}
                        className="w-full bg-transparent px-2 py-2 text-[10px] font-black text-gray-700 uppercase tracking-widest outline-none focus:text-indigo-600 text-center"
                      />
                      <button 
                        onClick={() => removeColumn(col)}
                        className="opacity-0 group-hover/col:opacity-100 text-gray-300 hover:text-rose-500 transition-all p-2 mr-1 border-l border-gray-100"
                        title="Remove Column"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  </th>
                ))}
                <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest w-20 text-center">Row</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {activeSheet.rows.map((row, index) => (
                <tr key={row.id} className="group hover:bg-indigo-50/30 transition-colors">
                  {activeSheet.columns.map(col => (
                    <td key={col} className="px-4 py-2">
                      <input
                        type="text"
                        value={row[col]}
                        onChange={(e) => handleCellChange(row.id, col, e.target.value)}
                        placeholder={`...`}
                        className={`w-full bg-transparent border border-transparent group-hover:border-indigo-100 focus:border-indigo-500 rounded-xl px-4 py-2.5 text-sm font-medium outline-none transition-all focus:bg-white focus:shadow-md ${col === 'S.N.' ? 'text-center font-bold text-indigo-600' : ''}`}
                      />
                    </td>
                  ))}
                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={() => removeRow(row.id)}
                      className="p-2.5 text-gray-200 hover:text-rose-600 hover:bg-rose-100 rounded-2xl transition-all"
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Add Row Button */}
        <div className="p-6 bg-gray-50/50 border-t border-gray-100 flex flex-col md:flex-row items-center justify-center gap-4">
          <div className="flex items-center gap-2 bg-white p-2 rounded-2xl border border-gray-200 shadow-sm">
             <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest pl-3 pr-1">Rows to add:</span>
             <input 
               type="number" 
               min="1" 
               max="100"
               value={rowsToAddCount}
               onChange={(e) => setRowsToAddCount(e.target.value)}
               className="w-16 bg-gray-50 rounded-xl px-3 py-2 text-sm font-black text-indigo-600 outline-none border border-transparent focus:border-indigo-100"
             />
          </div>
          <button
            onClick={() => addRow(rowsToAddCount)}
            className="flex items-center gap-3 px-12 py-4 bg-white border-2 border-dashed border-gray-200 text-gray-500 hover:border-indigo-400 hover:text-indigo-600 hover:shadow-lg rounded-3xl font-black text-sm transition-all active:scale-95 shadow-sm"
          >
            <Plus size={24} /> ADD NEW DATA ROWS
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportStudents;
